import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Cookie, X } from 'lucide-react';

export const CookieBanner = () => {
  const navigate = useNavigate();
  const [showBanner, setShowBanner] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem('cookieConsent');
    
    if (!cookieConsent) {
      // Small delay for better UX
      setTimeout(() => {
        setShowBanner(true);
        setTimeout(() => setIsVisible(true), 100);
      }, 1000);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    closeBanner();
  };

  const handleDecline = () => {
    localStorage.setItem('cookieConsent', 'declined');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    closeBanner();
  };

  const handleManagePreferences = () => {
    navigate('/cookie-preferences');
    closeBanner();
  };

  const closeBanner = () => {
    setIsVisible(false);
    setTimeout(() => setShowBanner(false), 300);
  };

  if (!showBanner) return null;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-50 p-4 transition-all duration-300 ease-in-out ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
      }`}
    >
      <Card className="max-w-5xl mx-auto bg-white/95 backdrop-blur-sm border-2 border-primary/20 shadow-2xl">
        <div className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            {/* Icon */}
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                <Cookie className="w-6 h-6 text-primary" />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <h3 className="font-playfair font-bold text-lg sm:text-xl text-gray-900 mb-2">
                🍪 We Value Your Privacy
              </h3>
              <p className="font-outfit text-sm sm:text-base text-gray-600 leading-relaxed">
                We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
                By clicking "Accept All", you consent to our use of cookies.
                {' '}
                <button 
                  onClick={handleManagePreferences}
                  className="text-primary hover:underline font-medium"
                >
                  Manage preferences
                </button>
                {' or '}
                <a 
                  href="/faq" 
                  className="text-primary hover:underline font-medium"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  learn more
                </a>
                .
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto sm:flex-shrink-0">
              <Button
                onClick={handleDecline}
                variant="outline"
                className="w-full sm:w-auto font-outfit border-gray-300 hover:bg-gray-100"
              >
                Decline
              </Button>
              <Button
                onClick={handleAccept}
                className="w-full sm:w-auto font-outfit bg-primary hover:bg-primary/90 text-white"
              >
                Accept All
              </Button>
            </div>

            {/* Close button (optional - for better UX) */}
            <button
              onClick={handleDecline}
              className="absolute top-3 right-3 sm:relative sm:top-0 sm:right-0 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Additional info */}
          <div className="mt-3 pt-3 border-t border-gray-200">
            <p className="text-xs text-gray-500 font-outfit">
              By using DogtyDraw, you agree to our use of cookies for analytics and personalized content. 
              You can change your preferences at any time in your account settings.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};
